import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClientCheckSettings } from '@fkwd-qc/shared';
import { getClientCheckSettings, updateClientCheckSettings } from '@/lib/api';
import { CHECKS_QUERY_KEY, CLIENT_CHECK_SETTINGS_QUERY_KEY } from '@/lib/query-keys';

/**
 * Reads how one check is set up for one client, without throwing on failure.
 *
 * @since 1.5.0
 * @param clientSlug - client slug, or undefined to leave the query idle
 * @param checkSlug - check slug, or undefined to leave the query idle
 * @returns the settings query result
 */
export function useClientCheckSettings(clientSlug: string | undefined, checkSlug: string | undefined) {
    return useQuery({
        queryKey: [CLIENT_CHECK_SETTINGS_QUERY_KEY, clientSlug ?? null, checkSlug ?? null],
        queryFn: () => getClientCheckSettings(clientSlug ?? '', checkSlug ?? ''),
        enabled: clientSlug !== undefined && checkSlug !== undefined,
        throwOnError: false,
    });
}

/**
 * Stores how one check is set up for one client and refreshes the settings and the registry's
 * per-client counts.
 *
 * @since 1.5.0
 * @returns the update mutation
 */
export function useUpdateClientCheckSettings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            clientSlug,
            checkSlug,
            body,
        }: {
            clientSlug: string;
            checkSlug: string;
            body: ClientCheckSettings;
        }) => updateClientCheckSettings(clientSlug, checkSlug, body),
        onSuccess: async (_settings, variables) => {
            await queryClient.invalidateQueries({
                queryKey: [CLIENT_CHECK_SETTINGS_QUERY_KEY, variables.clientSlug, variables.checkSlug],
            });
            await queryClient.invalidateQueries({ queryKey: CHECKS_QUERY_KEY });
        },
    });
}
